<template>
    <div class="flex flex-col gap-y-2">
        <div class="flex items-center justify-between">
            <div class="text-2xl font-medium text-gray-900">
                {{ title }}
            </div>

            <prime-button
                text
                icon="pi pi-pencil"
                :label="t('edit')"
                :class="editing ? 'invisible' : ''"
                :disabled="isProcessing"
                @click="$emit('update:editing', true)"
            />
        </div>

        <div
            v-if="!editing"
            class="grid grid-cols-2 gap-y-2"
        >
            <div
                v-if="isFullAddress"
                class="col-span-2"
            >
                <div class="text-sm font-medium text-gray-500">
                    {{ t('address.address') }}
                </div>
                <div class="text-gray-900">
                    {{ address1 }}
                </div>
                <div v-if="address2 !== ''">
                    {{ address2 }}
                </div>
            </div>

            <div v-if="isFullAddress">
                <div class="text-sm font-medium text-gray-500">
                    {{ t('address.city') }}
                </div>
                <div class="text-gray-900">
                    {{ city }}
                </div>
            </div>

            <div v-if="isFullAddress">
                <div class="text-sm font-medium text-gray-500">
                    {{ t('address.state') }}
                </div>
                <div class="text-gray-900">
                    {{ state }}
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    {{ t('address.postalCode') }}
                </div>
                <div class="text-gray-900">
                    {{ postal }}
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    {{ t('address.country') }}
                </div>
                <div class="text-gray-900">
                    {{ country }}
                </div>
            </div>
        </div>

        <div
            v-else
            class="grid grid-cols-12 gap-x-4 gap-y-1"
        >
            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('address.country') }}
                </div>
                <prime-auto-complete
                    id="country"
                    v-model="selectedCountry"
                    autocomplete="country"
                    :placeholder="t('address.country')"
                    option-label="name"
                    data-key="code"
                    :suggestions="filteredCountries"
                    dropdown
                    type="text"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{ input: { class: 'w-full' } }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @complete="searchCountry"
                />
                <div class="text-red-600 text-sm">
                    {{ errorCountry || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('address.lineOne') }}
                </div>
                <input-text
                    id="billing_address_line1"
                    v-model="address1"
                    :placeholder="t('address.lineOne')"
                    autocomplete="billing address-line1"
                    type="text"
                    class="w-full"
                    :class="errorAddress1 ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorAddress1 || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12"
            >
                <div class="text-sm text-gray-500">
                    {{ t('checkoutSections.addressLineTwoOptional') }}
                </div>
                <input-text
                    id="billing_address_line2"
                    v-model="address2"
                    :placeholder="t('address.lineTwo')"
                    autocomplete="billing address-line2"
                    type="text"
                    class="w-full"
                    :class="errorAddress2 ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorAddress2 || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12 lg:col-span-6"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('address.city') }}
                </div>
                <input-text
                    id="city"
                    v-model="city"
                    autocomplete="billing address-level2"
                    :placeholder="t('address.city')"
                    type="text"
                    class="w-full"
                    :class="errorCity ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorCity || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12 lg:col-span-6"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{
                        isUnitedStates
                            ? t('address.state')
                            : t('address.stateOrProvince')
                    }}
                </div>
                <prime-dropdown
                    v-if="isUnitedStates"
                    v-model="state"
                    option-label="name"
                    option-value="code"
                    :options="unitedStatesCodes"
                    class="!w-full"
                    :class="errorState ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <input-text
                    v-else
                    id="state"
                    v-model="state"
                    autocomplete="billing address-level1"
                    :placeholder="t('address.stateOrProvince')"
                    type="text"
                    class="w-full"
                    :class="errorState ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorState || '&nbsp;' }}
                </div>
            </div>
            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('address.postalCode') }}
                </div>
                <input-text
                    id="postal_code"
                    v-model="postal"
                    autocomplete="postal-code"
                    :placeholder="t('address.postalCode')"
                    type="text"
                    class="w-full"
                    :class="errorPostal ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorPostal || '&nbsp;' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { boolean, object, string } from 'yup'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'
import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'

type TranslationFunction = (key: string, ...args: unknown[]) => string

export const BillingAddressCustomerFragment = graphql(/* GraphQL */ `
    fragment BillingAddressCustomerFragment on Customer {
        address1
        address2
        city
        state
        postal
        country
    }
`)

export function BillingAddressSchema(t: TranslationFunction) {
    return object({
        fullAddress: boolean().required(),
        address1: string()
            .label(t('address.lineOne'))
            .when('fullAddress', {
                is: true,
                then: () =>
                    string().required(t('address.streetAddressRequired'))
            }),
        address2: string()
            .label(t('address.lineTwo'))
            .when('fullAddress', {
                is: true,
                then: () => string()
            }),
        city: string()
            .label(t('address.city'))
            .when('fullAddress', {
                is: true,
                then: () => string().required(t('address.cityRequired'))
            }),
        state: string()
            .label(t('address.state'))
            .when('fullAddress', {
                is: true,
                then: () => string().required(t('address.stateRequired'))
            }),
        postal: string()
            .required(t('address.invalidPostalCodeWarning'))
            .label(t('address.postalCode')),
        country: string()
            .required(t('address.invalidCountryWarning'))
            .label(t('address.country'))
    }).required()
}

const translationStub: TranslationFunction = () => ''

export function BillingAddressInitialValues(
    customer: FragmentType<typeof BillingAddressCustomerFragment> | null,
    isFullAddress: boolean
) {
    const customerData =
        customer === null
            ? null
            : getFragmentData(BillingAddressCustomerFragment, customer)

    return {
        fullAddress: isFullAddress,
        address1: customerData?.address1 ?? '',
        address2: customerData?.address2 ?? '',
        city: customerData?.city ?? '',
        state: customerData?.state ?? '',
        postal: customerData?.postal ?? '',
        country: customerData?.country ?? ''
    }
}

const billingAddressSchemaTyped = toTypedSchema(
    BillingAddressSchema(translationStub)
)
export type BillingAddressSchemaTyped =
    typeof billingAddressSchemaTyped extends TypedSchema<unknown, infer TOutput>
        ? TOutput
        : never
</script>

<script setup lang="ts">
import unitedStatesCodes from 'assets/us-code.json'
import InputText from 'primevue/inputtext'
import PrimeDropdown from 'primevue/dropdown'
import PrimeButton from 'primevue/button'
import PrimeAutoComplete from 'primevue/autocomplete'
import { computed, onMounted, ref, watch } from 'vue'
import { useField } from 'vee-validate'
import countryCodes from 'assets/country-code.json'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useShowAlert } from '#imports'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    customer: FragmentType<typeof BillingAddressCustomerFragment> | null
    isFullAddress: boolean
    title: string
    editing: boolean
}>()

const emit = defineEmits<{
    (e: 'update:editing', payload: boolean): void
}>()

const { t } = useI18n({ useScope: 'global' })

const customerData = computed(() =>
    props.customer === null
        ? null
        : getFragmentData(BillingAddressCustomerFragment, props.customer)
)

const hasCompleteAddress = computed(() => {
    if (customerData.value === null) {
        return false
    }

    if (!props.isFullAddress) {
        return (
            customerData.value.postal !== '' &&
            customerData.value.country !== ''
        )
    }

    return (
        customerData.value.address1 !== '' &&
        customerData.value.city !== '' &&
        customerData.value.state !== '' &&
        customerData.value.postal !== '' &&
        customerData.value.country !== ''
    )
})

emit('update:editing', !hasCompleteAddress.value)
const { value: address1, errorMessage: errorAddress1 } =
    useField('address.address1')
const { value: address2, errorMessage: errorAddress2 } =
    useField('address.address2')
const { value: city, errorMessage: errorCity } = useField('address.city')
const { value: state, errorMessage: errorState } = useField('address.state')
const { value: postal, errorMessage: errorPostal } = useField('address.postal')
const { value: country, errorMessage: errorCountry } =
    useField('address.country')

watch(
    [
        errorAddress1,
        errorAddress2,
        errorCity,
        errorState,
        errorPostal,
        errorCountry
    ],
    ([
        newValueAddress1,
        newValueAddress2,
        newValueCity,
        newValueState,
        newValuePostal,
        newValueCountry
    ]) => {
        if (
            newValueAddress1 ||
            newValueAddress2 ||
            newValueCity ||
            newValueState ||
            newValuePostal ||
            newValueCountry
        ) {
            emit('update:editing', true)
        }
    }
)

const unitedStatesOption = { code: 'US', name: 'United States' }
const countryOptions = [
    unitedStatesOption,
    ...(countryCodes as unknown as { code: string; name: string }[]).filter(
        option => option.code !== 'US'
    )
]
const filteredCountries = ref<{ code: string; name: string }[]>([])
const selectedCountry = ref<{ code: string; name: string } | string>(
    countryOptions.find(option => option.code === country.value) ?? ''
)
const isUnitedStates = computed(() => country.value === 'US')

const showAlert = useShowAlert()
watch(selectedCountry, (newValue: { code: string; name: string } | string) => {
    // If newValue is an object (they clicked one of the options in the list)
    if (typeof newValue === 'object') {
        if (newValue.code.length > 64) {
            showAlert(
                t('checkoutSections.error'),
                t('address.countryCodeLengthWarning')
            )
            country.value = undefined
        } else {
            country.value = newValue.code
        }
    }
    // Else newValue is a string (it came from typing)
    else {
        // If they typed one of the country names (case-insensitive)
        // Then we select that country for them
        const matchingCountry = countryOptions.find(
            option => option.name.toLowerCase() === newValue.toLowerCase()
        )
        if (matchingCountry === undefined) {
            country.value = undefined
        } else {
            country.value = matchingCountry.code
            selectedCountry.value = matchingCountry
        }
    }
})

function searchCountry(event: { query: string }): void {
    if (!event.query.trim().length) {
        filteredCountries.value = [...countryOptions]
    } else {
        filteredCountries.value = countryOptions.filter(option => {
            return (
                option.name
                    .toLowerCase()
                    .startsWith(event.query.toLowerCase()) ||
                option.code.toLowerCase().startsWith(event.query.toLowerCase())
            )
        })
    }
}

const activeFacility = useActiveFacility()
onMounted(() => {
    if (!hasCompleteAddress.value) {
        const facilityCountry = countryOptions.find(
            country => country.code === activeFacility.value.country
        )
        if (facilityCountry === undefined) {
            throw new Error('Cannot find facility setting country')
        }

        selectedCountry.value = facilityCountry
    }
})

const { isProcessing } = useCheckoutFormProcessing()
</script>
